import type { TFunction } from 'i18next'

import type { Allocation, AllocationTargetKind, Receivable } from '@/types/domain'

/* What a receivable is called on screen (S-21): «Счёт AG-INV-17»,
 * «Дебет-нота AG-DN-4», «Транш 2 из 2».
 *
 * Composed HERE from the facts the server serves — a document's number, a
 * tranche's place in its whole series — and never from the server's
 * `targetLabel`, which is English written for a log ("tranche 2 of 2",
 * AD-27). The same words for the same document wherever it appears: a row of
 * «Счета клиенту», a row of the client payment sheet, an allocation under
 * «Полученные платежи», a refusal naming what it refused.
 *
 * A cost line has no facts (its label stays the server's machine label, and
 * for Sales the vendor stays withheld), and an allocation served before its
 * facts existed has none either — both fall back to `targetLabel` as served.
 * A module rather than a component file, because a component file may export
 * only components. */

/** The facts a label is made of, as both the receivables read and an
 *  allocation (or a refusal's members) carry them. */
export interface TargetFacts {
  number?: string
  sequence?: number
  of?: number
}

/** The label for a target kind and its facts, or `undefined` when the facts
 *  that kind needs are missing — the caller's cue to fall back. */
export function targetLabelOf(
  t: TFunction,
  kind: AllocationTargetKind | string,
  facts: TargetFacts,
): string | undefined {
  if (kind === 'invoice' && facts.number) {
    return t('finance.receivables.invoice', { number: facts.number })
  }
  if (kind === 'debit_note' && facts.number) {
    return t('finance.receivables.debitNote', { number: facts.number })
  }
  if (kind === 'tranche' && facts.sequence !== undefined && facts.of !== undefined) {
    return t('finance.receivables.tranche', { sequence: facts.sequence, of: facts.of })
  }
  return undefined
}

/** One row of the receivables read, by its own facts. */
export function receivableLabel(t: TFunction, row: Receivable): string {
  const label =
    row.kind === 'tranche'
      ? targetLabelOf(t, row.kind, { sequence: row.sequence, of: row.of })
      : targetLabelOf(t, row.kind, { number: row.number })
  // Every row of the read carries its facts; the id is the last resort.
  return label ?? row.id
}

/** One allocation: by its facts when it has them, else `targetLabel` as the
 *  server resolved it — a cost line, or a row from before the facts. */
export function allocationLabel(t: TFunction, allocation: Allocation): string {
  return (
    targetLabelOf(t, allocation.targetKind, {
      number: allocation.targetNumber,
      sequence: allocation.targetSequence,
      of: allocation.targetOf,
    }) ?? allocation.targetLabel
  )
}

/** Whether an allocation settles a RECEIVABLE — an invoice, a debit note or a
 *  tranche — the kinds a reversal takes (S-21). A cost line's split is the
 *  carrier sheet's, and the server refuses to reverse it. */
export function isReceivableAllocation(allocation: Pick<Allocation, 'targetKind'>): boolean {
  return (
    allocation.targetKind === 'invoice' ||
    allocation.targetKind === 'debit_note' ||
    allocation.targetKind === 'tranche'
  )
}
